import { Box, Chip, CircularProgress, Grid, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IndicatorCard } from '../../HomePrivado/components/Indicators/IndicatorCard';
import { VISIT_TABS_INDEX } from '../../../redux/slices/visitsSlice';
import { VisitaEstado } from '../consts/visitasStatus';

interface IndicadorEstadoData {
    count?: number;
    roam?: number;
    preventivas?: number;
}

interface IndicatorsData {
    pendientes?: IndicadorEstadoData;
    programadas?: IndicadorEstadoData;
    enProgreso?: IndicadorEstadoData;
    enRevision?: IndicadorEstadoData;
    presentadas?: IndicadorEstadoData;
}

interface VisitasIndicatorsProps {
    indicatorsData: IndicatorsData | undefined;
    // isRoam distingue a qué grilla navegar: Visitas (Preventivas) o Visitas ROAM.
    onFilterByEstado: (estadoId: number, tabIndex: VISIT_TABS_INDEX, isRoam: boolean) => void;
    isLoading?: boolean;
}

const IndicatorFooterItem = ({
    value,
    label,
    bgColor,
    textColor,
    side,
    loading = false,
    onClick,
    testID
}: {
    value: number;
    label: string;
    bgColor: string;
    textColor: string;
    side: 'left' | 'right';
    loading?: boolean;
    onClick: () => void;
    testID: string;
}) => (
    <Box
        onClick={(event) => {
            event.stopPropagation();
            onClick();
        }}
        data-testid={testID}
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flex: 1,
            padding: '10px 12px',
            cursor: 'pointer',
            backgroundColor: bgColor,
            borderTopLeftRadius: side === 'left' ? '8px' : 0,
            borderBottomLeftRadius: side === 'left' ? '8px' : 0,
            borderTopRightRadius: side === 'right' ? '8px' : 0,
            borderBottomRightRadius: side === 'right' ? '8px' : 0,
            '&:hover': { filter: 'brightness(0.97)' }
        }}
    >
        <Box>
            {loading ? (
                <CircularProgress size={20} sx={{ color: textColor }} />
            ) : (
                <Typography sx={{ fontSize: '20px', fontWeight: '700', lineHeight: 1 }}>
                    {value}
                </Typography>
            )}
            <Typography sx={{ fontSize: '13px', color: textColor }}>{label}</Typography>
        </Box>
        <ChevronRightIcon sx={{ color: textColor }} />
    </Box>
);

export const VisitasIndicators = ({
    indicatorsData,
    onFilterByEstado,
    isLoading = false
}: VisitasIndicatorsProps) => {
    const cards = [
        {
            value: indicatorsData?.pendientes?.count ?? 0,
            roam: indicatorsData?.pendientes?.roam ?? 0,
            preventivas: indicatorsData?.pendientes?.preventivas ?? 0,
            estadoId: 1,
            tabIndex: VISIT_TABS_INDEX.PENDING,
            footerBg: '#F3F4F6',
            footerTextColor: '#6B7280',
            chip: {
                label: 'Pendientes',
                sx: {
                    background: '#F3F4F6',
                    border: '1px solid #6B7280',
                    color: '#6B7280'
                }
            }
        },
        {
            value: indicatorsData?.programadas?.count ?? 0,
            roam: indicatorsData?.programadas?.roam ?? 0,
            preventivas: indicatorsData?.programadas?.preventivas ?? 0,
            estadoId: 8,
            tabIndex: VISIT_TABS_INDEX.PROGRAMMED,
            footerBg: '#FFF3C4',
            footerTextColor: '#FFA000',
            chip: {
                label: 'Programadas',
                sx: {
                    background: '#FFF3C4',
                    border: '1px solid #FFA000',
                    color: '#FFA000'
                }
            }
        },
        {
            value: indicatorsData?.enProgreso?.count ?? 0,
            roam: indicatorsData?.enProgreso?.roam ?? 0,
            preventivas: indicatorsData?.enProgreso?.preventivas ?? 0,
            estadoId: 9,
            tabIndex: VISIT_TABS_INDEX.IN_PROGRESS,
            footerBg: '#E0F2FE',
            footerTextColor: '#0284C7',
            chip: {
                label: 'En progreso',
                sx: {
                    background: '#E0F2FE',
                    border: '1px solid #0284C7',
                    color: '#0284C7'
                }
            }
        },
        {
            value: indicatorsData?.enRevision?.count ?? 0,
            roam: indicatorsData?.enRevision?.roam ?? 0,
            preventivas: indicatorsData?.enRevision?.preventivas ?? 0,
            estadoId: VisitaEstado.REALIZADA,
            tabIndex: VISIT_TABS_INDEX.IN_REVISION,
            footerBg: '#F3E8FF',
            footerTextColor: '#9333EA',
            chip: {
                label: 'En revisión',
                sx: {
                    background: '#F3E8FF',
                    border: '1px solid #9333EA',
                    color: '#9333EA'
                }
            }
        },
        {
            value: indicatorsData?.presentadas?.count ?? 0,
            roam: indicatorsData?.presentadas?.roam ?? 0,
            preventivas: indicatorsData?.presentadas?.preventivas ?? 0,
            estadoId: VisitaEstado.PRESENTADO,
            tabIndex: VISIT_TABS_INDEX.CLOSED,
            footerBg: '#F0FDF4',
            footerTextColor: '#166534',
            chip: {
                label: 'Presentadas',
                sx: {
                    background: '#F0FDF4',
                    border: '1px solid #166534',
                    color: '#166534'
                }
            }
        }
    ];

    return (
        <Grid container spacing={2} mb={5}>
            {cards.map((card, index) => (
                <Grid item xs={6} md={2.4} key={index}>
                    <IndicatorCard
                        value={card.value}
                        loading={isLoading}
                        inlineHeader
                        height="190px"
                        testID={`indicator-card-${card.estadoId}`}
                        footer={
                            <Grid
                                container
                                wrap="nowrap"
                                data-testid={`indicator-card-${card.estadoId}-footer`}
                            >
                                <IndicatorFooterItem
                                    value={card.preventivas}
                                    label="Preventivas"
                                    bgColor={card.footerBg}
                                    textColor={card.footerTextColor}
                                    side="left"
                                    loading={isLoading}
                                    testID={`indicator-card-${card.estadoId}-preventivas`}
                                    onClick={() =>
                                        onFilterByEstado(card.estadoId, card.tabIndex, false)
                                    }
                                />
                                <Box sx={{ width: '1px', backgroundColor: card.footerTextColor }} />
                                <IndicatorFooterItem
                                    value={card.roam}
                                    label="ROAM"
                                    bgColor={card.footerBg}
                                    textColor={card.footerTextColor}
                                    side="right"
                                    loading={isLoading}
                                    testID={`indicator-card-${card.estadoId}-roam`}
                                    onClick={() =>
                                        onFilterByEstado(card.estadoId, card.tabIndex, true)
                                    }
                                />
                            </Grid>
                        }
                    >
                        <Chip {...card.chip} />
                    </IndicatorCard>
                </Grid>
            ))}
        </Grid>
    );
};
